import { ErrorService } from '../../../../core/services/error.service';
import { GroupSerieService } from '../../services/group-serie.service';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { GroupSeriesDto } from '../../dtos/group-series.dto';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DatePipe } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { GroupSeriesDataDto } from '../../dtos/group-series-data.dto';
import { GroupSeriesFormFactory } from '../../mappers/group-series-form.factory';
import { UpdateGroupSeriesDto } from '../../dtos/update-group-series.dto';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-group-serie',
  imports: [MatDialogModule, MatCardModule, MatIconModule, MatButtonModule, MatFormFieldModule, DatePipe, MatCheckboxModule, MatInputModule, ReactiveFormsModule, TranslocoPipe],
  templateUrl: './manage-group-series.dialog.html',
  styleUrl: './manage-group-series.dialog.scss'
})
export class ManageGroupSeriesDialog implements OnInit {
  groupSeriesForm!: FormGroup;

  imagePreview?: string;
  imageError?: string;
  selectedFile?: File;

  hadImage!: boolean;
  imageHasChanged: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { groupSeriesDto: GroupSeriesDto },
    private dialogRef: MatDialogRef<ManageGroupSeriesDialog>,
    private fb: FormBuilder,
    private groupSeriesService: GroupSerieService,
    private translocoService: TranslocoService,
    private errorService: ErrorService
  ) {
  }

  ngOnInit(): void {
    this.imagePreview = this.data.groupSeriesDto.imageUrl;
    this.hadImage = this.imagePreview != undefined;
    this.groupSeriesForm = GroupSeriesFormFactory.createGroupSeriesForm(this.fb, (this.data.groupSeriesDto as GroupSeriesDataDto));
  }

  selectFile(event: any): void  {
    const input : HTMLInputElement = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        //image dimension check
        const maxDimension = 1500;
        if (img.width > maxDimension || img.height > maxDimension) {
          this.imageError = this.translocoService.translate('error.image.dimension', {width: img.width, height: img.height, maxDimension: maxDimension});
          this.imageHasChanged = false;
          return;
        }

        //image size check
        const maxSize = 1024 * 1024;
        if (file.size > maxSize) {
          this.imageError = this.translocoService.translate('error.image.size', {size: (file.size / 1024 / 1024).toFixed(1), maxSize: 1});
          this.imageHasChanged = false;
          return;
        }

        const formData = new FormData();
        formData.append('imageFile', file, file.name);

        this.imagePreview = reader.result as string;
        this.imageError = undefined;
      };

      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
    this.selectedFile = file;
    this.imageHasChanged = true;
  }

  deleteImage(): void {
    this.imagePreview = undefined;
    this.selectedFile = undefined;
    this.imageError = undefined;
    this.imageHasChanged = true;
  }

  close(): void {
    this.dialogRef.close(false);
  }

  manageImage(updateGroupSeriesDto: UpdateGroupSeriesDto): void {
    if (!this.imagePreview) {
      if (this.hadImage) {
        this.groupSeriesService.deleteGroupSerieImage(updateGroupSeriesDto.groupSeriesId).subscribe({
          next: () => {
            this.dialogRef.close(true);
          },
          error: (error) => {
            this.errorService.handleError(error, this.translocoService.translate("error.title.delete_image"));
          }
        });
        return;
      }

      this.dialogRef.close(true);
      return;
    }

    if (!this.imageHasChanged || !this.selectedFile) {
      this.dialogRef.close(true);
      return;
    }

    let formData: FormData = new FormData();
    formData.append('imageFile', this.selectedFile, this.selectedFile.name);

    this.groupSeriesService.uploadGroupSerieImage(this.data.groupSeriesDto.groupSeriesId, formData).subscribe({
      next: () => {
        this.dialogRef.close(true);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.add_image"));
      }
    });
  }

  save(): void {
    if (this.groupSeriesForm.invalid) {
      this.groupSeriesForm.markAllAsTouched();
      return;
    }

    let updatedGroupSeriesDto: GroupSeriesDto = {
      groupSeriesId: this.data.groupSeriesDto.groupSeriesId,
      ...this.groupSeriesForm.value
    };
    this.groupSeriesService.updateGroupSerie(updatedGroupSeriesDto).subscribe({
      next: () => {
        this.manageImage(updatedGroupSeriesDto);
      },
      error: (error) => {
        this.errorService.handleError(error, this.translocoService.translate("error.title.edit_group_series"));
      }
    });
  }
}
